import StyledDrawer from '@/components/Hub2.0/Reusable/StyledDrawer';
import AutoComplete from '@/components/Reusable/Search/AutoComplete';
import useHubTranslation from '@/utils/i18n/useHubTranslation';
import { Box } from '@mui/material';
import Fuse from 'fuse.js';
import { useRouter } from 'next/router';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Markdown from 'react-markdown';

export interface DrawerHelpPanelProps {
  open: boolean;
  onClose: () => void;
  helpPanels: Record<string, string>;
  helpPanelsLoading: boolean;
}

type HelpPanelOption = {
  label: string;
  value: string;
  group: string;
  showByDefault: boolean;
  parent?: HelpPanelOption;
};

const HELP_PANEL_DRAWER_WIDTH = 'min(560px, 90vw)';

const DrawerHelpPanel: React.FC<DrawerHelpPanelProps> = ({
  open,
  onClose,
  helpPanels = {},
  helpPanelsLoading,
}) => {
  const { translateHubUi } = useHubTranslation();
  const [dropDownOptions, setDropDownOptions] = useState<HelpPanelOption[]>([]);
  const [displayedHelpPanel, setDisplayedHelpPanel] = useState<
    HelpPanelOption | undefined
  >(undefined);
  const router = useRouter();

  // Memoize the key - this will only change when helpPanels keys change
  // Using sorted keys instead of JSON.stringify for better performance with large objects
  const helpPanelsKey = useMemo(
    () => Object.keys(helpPanels).sort().join(','),
    [helpPanels]
  );

  // Use ref to store stable helpPanels reference
  const stableHelpPanelsRef = useRef<Record<string, string>>(helpPanels);

  // Update ref when helpPanels changes (handles both key and value changes)
  useEffect(() => {
    stableHelpPanelsRef.current = helpPanels;
  }, [helpPanels]);

  // Stabilize router.query.tab to prevent unnecessary effect triggers
  const routerTab = useMemo(() => {
    return router.query.tab;
  }, [router.query.tab]);

  const buildDropDownOption = useCallback(
    (panel: string): HelpPanelOption => {
      const fallbackLabel = translateHubUi('nav.help.fallbackLabel', 'N/A');
      if (!panel) {
        return {
          label: fallbackLabel,
          value: '',
          group: fallbackLabel,
          showByDefault: true,
        };
      }
      const titleLine = panel.split('\n')[0].replace('#', '').trim();
      const [group, label] = titleLine.split(' - ');
      return {
        label: label || fallbackLabel,
        value: panel,
        group: group || fallbackLabel,
        showByDefault: true,
      };
    },
    [translateHubUi]
  );

  const setPanelForCurrentPage = useCallback((): void => {
    // Access current value from ref to ensure we have latest
    const currentHelpPanels = stableHelpPanelsRef.current;
    if (!currentHelpPanels || helpPanelsLoading) {
      const emptyOption = buildDropDownOption('');
      setDisplayedHelpPanel((prev) => {
        // Guard: only update if value actually changed
        if (
          prev?.label === emptyOption.label &&
          prev?.value === emptyOption.value
        ) {
          return prev;
        }
        return emptyOption;
      });
      return;
    }
    const path = router.pathname.slice(1); // Remove leading slash.
    const tab = routerTab;
    const keyWithTab = tab ? `${path}/${tab}.md` : null;
    const newPanel = buildDropDownOption(
      (keyWithTab && currentHelpPanels[keyWithTab]) ||
        currentHelpPanels[`${path}/default.md`] ||
        currentHelpPanels['default.md'] ||
        ''
    );
    setDisplayedHelpPanel((prev) => {
      // Guard: only update if value actually changed
      if (prev?.label === newPanel.label && prev?.value === newPanel.value) {
        return prev;
      }
      return newPanel;
    });
  }, [helpPanelsKey, helpPanelsLoading, router.pathname, routerTab]);

  function parseHelpPanels(panels: string[]): HelpPanelOption[] {
    return panels
      .map((panel) => {
        const basePanel = buildDropDownOption(panel);
        const sections = panel.split('## ');
        const sectionPanels = sections.slice(1).map((section) => {
          const naiveOption = buildDropDownOption(section);
          return {
            ...naiveOption,
            label: `${basePanel.label} - ${naiveOption.group}`,
            group: basePanel.group,
            showByDefault: false,
            parent: basePanel,
          };
        });
        return [basePanel, ...sectionPanels];
      })
      .flat()
      .sort((a, b) => a.group.localeCompare(b.group));
  }

  function filterOptions(
    options: HelpPanelOption[],
    state: { inputValue: string }
  ): HelpPanelOption[] {
    if (state.inputValue.length === 0)
      return options.filter((e) => e.showByDefault);
    const fuse = new Fuse(
      options.filter((e) => !e.showByDefault),
      {
        threshold: 0.4,
        keys: ['value', 'label'],
        ignoreLocation: true,
      }
    );
    return fuse
      .search(state.inputValue)
      .map((e) => e.item)
      .sort((a, b) => a.group.localeCompare(b.group));
  }

  useEffect(() => {
    const currentHelpPanels = stableHelpPanelsRef.current;
    if (
      helpPanelsLoading ||
      !currentHelpPanels ||
      Object.keys(currentHelpPanels).length === 0
    )
      return;
    const parsedOptions = parseHelpPanels(Object.values(currentHelpPanels));
    setDropDownOptions(parsedOptions);
  }, [helpPanelsLoading, helpPanelsKey]);

  useEffect(() => {
    if (helpPanelsLoading) return;
    setPanelForCurrentPage();
  }, [helpPanelsLoading, setPanelForCurrentPage]);

  return (
    <StyledDrawer
      open={open}
      hideBackdrop={false}
      onClose={onClose}
      width={HELP_PANEL_DRAWER_WIDTH}
    >
      <Box p={2}>
        <AutoComplete
          options={dropDownOptions}
          label={translateHubUi('nav.help.searchLabel', 'Search help articles')}
          groupBy='group'
          width='100%'
          onChange={(_: unknown, newValue: HelpPanelOption) => {
            if (newValue?.parent) {
              newValue = newValue.parent;
            }
            setDisplayedHelpPanel(newValue);
          }}
          value={displayedHelpPanel}
          filterOptions={filterOptions}
          isOptionEqualToValue={(
            option: HelpPanelOption,
            value: HelpPanelOption
          ) => option.label === value.label}
          placeholder={translateHubUi(
            'nav.help.searchPlaceholder',
            'Type to search...'
          )}
          loading={helpPanelsLoading}
          helperText={translateHubUi(
            'nav.help.searchHelperText',
            'Find help articles by keyword.'
          )}
          getOptionLabel={(option: HelpPanelOption) => option.label}
          getOptionKey={(option: HelpPanelOption) => option.label}
          renderOption={(
            props: React.HTMLAttributes<HTMLLIElement>,
            option: HelpPanelOption
          ) => <li {...props}>{option.label}</li>}
          multiple={false}
          freeSolo={false}
          renderTags={() => null}
        />
        <Markdown>{displayedHelpPanel?.value ?? ''}</Markdown>
      </Box>
    </StyledDrawer>
  );
};

export default DrawerHelpPanel;
